import React, { ChangeEvent, useContext, useMemo, useRef } from 'react';
import { Can } from '@casl/react';
import {
  Box,
  Card,
  CardHeader,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import RefreshTwoToneIcon from '@mui/icons-material/RefreshTwoTone';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { useTranslation } from 'react-i18next';
import AbilityContext from '@/contexts/AbilityContext';
import { Action } from '@/types/permissions';
import { Filters } from '@/types/Filters';
import { CustomerInvoiceProfile } from '@/types/CustomerInvoiceProfile.interface';
import { useTableFilters } from '@/hooks/useTableFilters';
import NoDataFound from '@/components/NoDataFound';
import { debounce } from '@/utils/helpers';
import { PAGE_SIZE_OPTIONS } from '@/constants/pagination';

interface CustomerInvoiceProfilesTableProps {
  profiles: CustomerInvoiceProfile[];
  loading: boolean;
  totalCount: number;
  onFilterChange: (filters: Filters) => void;
  onEditProfile: (profile: CustomerInvoiceProfile) => void;
  onDeleteProfile: (profile: CustomerInvoiceProfile) => void;
  rowsPerPageOptions?: number[];
  pageKey?: string;
}

const CustomerInvoiceProfilesTable = ({
  profiles,
  loading,
  totalCount,
  onFilterChange,
  onEditProfile,
  onDeleteProfile,
  rowsPerPageOptions = PAGE_SIZE_OPTIONS,
  pageKey
}: CustomerInvoiceProfilesTableProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const ability = useContext(AbilityContext);

  const {
    filters,
    setFilters,
    handleSearch: handleSearchFilter
  } = useTableFilters({
    initialFilters: { search: '', limit: 10, page: 0 },
    onFilterChange,
    pageKey
  });

  const canEdit = ability.can(Action.Update, 'CustomerInvoiceProfile');
  const canDelete = ability.can(Action.Delete, 'CustomerInvoiceProfile');
  const showActions = canEdit || canDelete;

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ): void => {
    setFilters((prev) => ({ ...prev, page: newPage }));
    onFilterChange({ ...filters, page: newPage });
  };

  const handleLimitChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const newLimit = parseInt(event.target.value, 10);
    setFilters((prev) => ({ ...prev, limit: newLimit, page: 0 }));
    onFilterChange({ ...filters, page: 0, limit: newLimit });
  };

  // One debounced function for the lifetime of the table, so a pending timer is
  // never orphaned by a re-render. It reads the live handler (which closes over
  // the current filters) through a ref, so a status chip or page size changed
  // while the search is pending is not overwritten by stale filters.
  const searchHandlerRef = useRef(handleSearchFilter);
  searchHandlerRef.current = handleSearchFilter;
  const handleSearch = useMemo(
    () => debounce((value: string) => searchHandlerRef.current(value), 500),
    []
  );

  const tableHeaders: Array<{
    id: string;
    label: string;
    align?: 'left' | 'center' | 'right';
  }> = [
    { id: 'name', label: t('customerInvoiceProfiles.column.name') },
    { id: 'taxId', label: t('customerInvoiceProfiles.column.taxId') },
    { id: 'taxOffice', label: t('customerInvoiceProfiles.column.taxOffice') },
    { id: 'email', label: t('customerInvoiceProfiles.column.email') },
    { id: 'phone', label: t('customerInvoiceProfiles.column.phone') },
    { id: 'location', label: t('customerInvoiceProfiles.column.location') },
    ...(showActions
      ? [{ id: 'actions', label: t('actions'), align: 'center' as const }]
      : [])
  ];

  return (
    <Container disableGutters maxWidth={false} sx={{ maxWidth: '95%' }}>
      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="stretch"
        spacing={3}
      >
        <Grid item xs={12}>
          <Card>
            <CardHeader
              action={
                <Box display="flex" alignItems="center">
                  <FormControl sx={{ minWidth: 260 }}>
                    <OutlinedInput
                      placeholder={t(
                        'customerInvoiceProfiles.search.placeholder'
                      )}
                      defaultValue={filters.search}
                      onChange={(e) => handleSearch(e.target.value)}
                      startAdornment={
                        <InputAdornment position="start">
                          <SearchOutlinedIcon />
                        </InputAdornment>
                      }
                      size="small"
                    />
                  </FormControl>
                </Box>
              }
              title={
                <Box display="flex" alignItems="center">
                  <Typography variant="h4" component="span">
                    {t('customerInvoiceProfiles.list.title')}
                  </Typography>
                  <Tooltip arrow title={t('refresh.list')}>
                    <IconButton
                      aria-label={t('refresh.list')}
                      onClick={() => onFilterChange({ ...filters })}
                      sx={{
                        ml: 1,
                        '&:hover': {
                          background: theme.colors.primary.lighter
                        },
                        color: theme.palette.primary.main
                      }}
                    >
                      <RefreshTwoToneIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
            />
            <Divider />
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {tableHeaders.map((header) => (
                      <TableCell key={header.id} align={header.align ?? 'left'}>
                        {header.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={tableHeaders.length}>
                        <Box p={2} display="flex" justifyContent="center">
                          <CircularProgress />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : profiles.length > 0 ? (
                    profiles.map((profile) => (
                      <TableRow hover key={profile.id}>
                        <TableCell>
                          <Typography
                            variant="body1"
                            fontWeight="bold"
                            color="text.primary"
                            noWrap
                          >
                            {profile.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Typography variant="body2" noWrap>
                              {profile.taxId}
                            </Typography>
                            <Chip
                              label={t(
                                `customerInvoiceProfiles.entityType.${profile.entityType}`
                              )}
                              color={
                                profile.entityType === 'COMPANY'
                                  ? 'primary'
                                  : 'default'
                              }
                              variant="outlined"
                              size="small"
                            />
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap>
                            {profile.taxOffice}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap>
                            {profile.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap>
                            {profile.phone}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap>
                            {[profile.city, profile.district]
                              .filter(Boolean)
                              .join(' / ')}
                          </Typography>
                        </TableCell>
                        {showActions && (
                          <TableCell align="center">
                            <Stack
                              direction="row"
                              spacing={0.5}
                              justifyContent="center"
                            >
                              <Can
                                I={Action.Update}
                                a="CustomerInvoiceProfile"
                                ability={ability}
                              >
                                <Tooltip title={t('edit')} arrow>
                                  <IconButton
                                    aria-label={t('edit')}
                                    onClick={() => onEditProfile(profile)}
                                    sx={{
                                      '&:hover': {
                                        background: theme.colors.primary.lighter
                                      },
                                      color: theme.palette.primary.main
                                    }}
                                    size="small"
                                  >
                                    <EditTwoToneIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Can>
                              <Can
                                I={Action.Delete}
                                a="CustomerInvoiceProfile"
                                ability={ability}
                              >
                                <Tooltip title={t('delete')} arrow>
                                  <IconButton
                                    aria-label={t('delete')}
                                    onClick={() => onDeleteProfile(profile)}
                                    sx={{
                                      '&:hover': {
                                        background: theme.colors.error.lighter
                                      },
                                      color: theme.palette.error.main
                                    }}
                                    size="small"
                                  >
                                    <DeleteTwoToneIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Can>
                            </Stack>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  ) : (
                    <NoDataFound
                      message={t('customerInvoiceProfiles.no.data')}
                      colSpan={tableHeaders.length}
                    />
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box p={2}>
              <TablePagination
                component="div"
                count={totalCount || 0}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleLimitChange}
                page={filters.page || 0}
                rowsPerPage={filters.limit || 10}
                rowsPerPageOptions={rowsPerPageOptions}
              />
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default CustomerInvoiceProfilesTable;
